import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DropdownList } from '../../UI/base/DropdownList';
import { patientDisplayName } from '../../../services/patientLabelService';
import type {
  Patient,
  Room,
  StaffMember,
  WeekFilterState
} from '../../../types/weekOverview';
import styles from './CalendarFilterSidebar.module.css';

// One active filter condition shown in the summary card (and reused by the
// modal for its title and print header).
export interface ActiveFilterSummary {
  type: 'staff' | 'room' | 'patient';
  typeLabel: string;
  value: string;
}

interface CalendarFilterSidebarProps {
  staff: StaffMember[];
  rooms: Room[];
  patients: Patient[];
  filters: WeekFilterState;
  activeFilters: ActiveFilterSummary[];
  filteredEventCount: number;
  onFiltersChange: (filters: WeekFilterState) => void;
}

interface ResourceOption {
  id: string | null;
  label: string;
  isSeparator?: boolean;
}

// Keys used for synthetic options — resource ids are backend-generated and
// never collide with them.
const ALL_OPTION_KEY = 'all';
const SEPARATOR_OPTION_KEY = 'unassigned-separator';

// The sidebar is 280px wide; cap the dropdown panels so long names can't
// push the panel past the sidebar edge.
const FILTER_PANEL_MAX_WIDTH_PX = 240;

function buildOptions<T extends { id: string; name: string }>(
  list: T[],
  allLabel: string
): ResourceOption[] {
  return [{ id: null, label: allLabel }, ...list.map((item) => ({ id: item.id, label: item.name }))];
}

// An explicit "All" pick maps to null (no filter). The `undefined` guard is
// defensive only — the dropdown's single mode always emits exactly one key.
function selectedIdFromKeys(next: (string | number)[]): string | null {
  const key = next[0];
  if (key === undefined || key === ALL_OPTION_KEY) return null;
  return String(key);
}

export const CalendarFilterSidebar = ({
  staff,
  rooms,
  patients,
  filters,
  activeFilters,
  filteredEventCount,
  onFiltersChange
}: CalendarFilterSidebarProps) => {
  const { t } = useTranslation();

  const allLabel = t('week-overview-filter-all');

  const staffOptions = useMemo(() => buildOptions(staff, allLabel), [staff, allLabel]);
  const roomOptions = useMemo(() => buildOptions(rooms, allLabel), [rooms, allLabel]);

  // Real patients first, then a separator, then the unassigned (planned but
  // not yet filled) seats — inspectable like any patient, visually set apart.
  const patientOptions = useMemo(() => {
    const realPatients = patients.filter((p) => !p.isUnassigned);
    const unassigned = patients.filter((p) => p.isUnassigned);
    const options = buildOptions(realPatients, allLabel);
    if (unassigned.length > 0) {
      options.push(
        { id: SEPARATOR_OPTION_KEY, label: '', isSeparator: true },
        ...unassigned.map((p) => ({ id: p.id, label: patientDisplayName(p, t) }))
      );
    }
    return options;
  }, [patients, allLabel, t]);

  const hasActiveFilters = activeFilters.length > 0;

  return (
    <aside className={`${styles.sidebar} js-print-hide`}>
      <div className={styles.sidebarSection}>
        <span className={styles.sidebarLabel}>{t('week-overview-filter-staff')}</span>
        <DropdownList<ResourceOption>
          options={staffOptions}
          getKey={(option) => option.id ?? ALL_OPTION_KEY}
          renderLabel={(option) => option.label}
          value={[filters.staffId ?? ALL_OPTION_KEY]}
          onChange={(next) => onFiltersChange({ ...filters, staffId: selectedIdFromKeys(next) })}
          placeholder={t('week-overview-filter-staff-placeholder')}
          panelMaxWidth={FILTER_PANEL_MAX_WIDTH_PX}
        />
      </div>

      <div className={styles.sidebarSection}>
        <span className={styles.sidebarLabel}>{t('week-overview-filter-room')}</span>
        <DropdownList<ResourceOption>
          options={roomOptions}
          getKey={(option) => option.id ?? ALL_OPTION_KEY}
          renderLabel={(option) => option.label}
          value={[filters.roomId ?? ALL_OPTION_KEY]}
          onChange={(next) => onFiltersChange({ ...filters, roomId: selectedIdFromKeys(next) })}
          placeholder={t('week-overview-filter-room-placeholder')}
          panelMaxWidth={FILTER_PANEL_MAX_WIDTH_PX}
        />
      </div>

      <div className={styles.sidebarSection}>
        <span className={styles.sidebarLabel}>{t('week-overview-filter-patient')}</span>
        <DropdownList<ResourceOption>
          options={patientOptions}
          getKey={(option) => option.id ?? ALL_OPTION_KEY}
          renderLabel={(option) =>
            option.isSeparator ? (
              <span className={styles.optionSeparator} aria-hidden="true" />
            ) : (
              option.label
            )
          }
          disabledKeys={[SEPARATOR_OPTION_KEY]}
          value={[filters.patientId ?? ALL_OPTION_KEY]}
          onChange={(next) => onFiltersChange({ ...filters, patientId: selectedIdFromKeys(next) })}
          placeholder={t('week-overview-filter-patient-placeholder')}
          panelMaxWidth={FILTER_PANEL_MAX_WIDTH_PX}
        />
      </div>

      {hasActiveFilters && (
        <div className={styles.summaryCard}>
          <ul className={styles.summaryFilters}>
            {activeFilters.map((filter) => (
              <li key={filter.type} className={styles.summaryFilterRow}>
                <span className={styles.summaryFilterType}>{filter.typeLabel}</span>
                <span className={styles.summaryFilterValue}>{filter.value}</span>
              </li>
            ))}
          </ul>
          <p className={styles.summaryMeta}>
            {filteredEventCount} {t('week-overview-modal-events-count')}
          </p>
        </div>
      )}
    </aside>
  );
};
